import neo4j, { Driver } from "neo4j-driver";

import { Environment } from "@abacus/common";

import {
  CREATE_ABACUS_PROFILE,
  GET_IDENTITY_BY_EMAIL,
  GET_IDENTITY_BY_ID,
  GET_PROFILE_BY_TYPE_AND_IDENTITY_ID,
} from "./cyphers";
import {
  GetProfileByTypeAndIdentityIdRequest,
  GetIdentityByEmailRequest,
  Identity,
  Profile,
  HelloResponse,
  GetIdentityByIdRequest,
  CreateAbacusProfileRequest,
} from "./types";
import { Throttler } from "../../../@abacus/common/dist/cjs/index.cjs";

export interface Neo4JClientConfig {
  db?: string;
  env: Environment;
  host: string;
  isEncryptedConnection?: boolean;
  pass: string;
  port?: number;
  throttler?: Throttler;
  user: string;
}

export class Neo4JClient {
  readonly db?: string;
  private driver: Driver;
  readonly env: Environment;
  readonly host: string;
  readonly isEncryptedConnection: boolean;
  readonly port?: number;
  readonly throttler?: Throttler;

  constructor({
    db,
    env,
    host,
    isEncryptedConnection,
    pass,
    port,
    throttler,
    user,
  }: Neo4JClientConfig) {
    this.db = db;
    this.env = env;
    this.host = host;
    this.isEncryptedConnection = isEncryptedConnection ?? false;
    this.port = port;
    this.throttler = throttler;
    this.driver = neo4j.driver(this.getUrl(), neo4j.auth.basic(user, pass));
  }

  async createAbacusProfile({
    createdBy,
    identityId,
  }: CreateAbacusProfileRequest): Promise<Profile | undefined> {
    await this.throttler?.wait();
    const { records } = await this.driver.executeQuery(
      CREATE_ABACUS_PROFILE,
      {
        createdBy,
        identityId,
      },
      {
        database: this.db,
        routing: neo4j.routing.WRITE,
      },
    );
    return records[0]?.["_fields"][0].properties;
  }

  async getProfileByTypeAndIdentityId({
    identityId,
    profileType,
  }: GetProfileByTypeAndIdentityIdRequest): Promise<Profile | undefined> {
    await this.throttler?.wait();
    const { records } = await this.driver.executeQuery(
      GET_PROFILE_BY_TYPE_AND_IDENTITY_ID,
      {
        identityId,
        profileType,
      },
      {
        database: this.db,
        routing: neo4j.routing.READ,
      },
    );
    return records[0]?.["_fields"][0].properties;
  }

  async getIdentityByEmail({
    email,
  }: GetIdentityByEmailRequest): Promise<Identity | undefined> {
    await this.throttler?.wait();
    const { records } = await this.driver.executeQuery(
      GET_IDENTITY_BY_EMAIL,
      {
        email,
      },
      {
        database: this.db,
        routing: neo4j.routing.READ,
      },
    );
    return records[0]?.["_fields"][0].properties;
  }

  async getIdentityById({
    identityId,
  }: GetIdentityByIdRequest): Promise<Identity | undefined> {
    await this.throttler?.wait();
    const { records } = await this.driver.executeQuery(
      GET_IDENTITY_BY_ID,
      {
        identityId,
      },
      {
        database: this.db,
        routing: neo4j.routing.READ,
      },
    );
    return records[0]?.["_fields"][0].properties;
  }

  private getUrl(): string {
    const enc = this.isEncryptedConnection ? "+s" : "";
    const port = this.port == null ? "" : `:${this.port}`;
    return `neo4j${enc}://${this.env}-${this.host}${port}`;
  }

  async hello(): Promise<HelloResponse> {
    await this.throttler?.wait();
    await this.driver.getServerInfo();
    return { status: "OK" };
  }

  terminate(): Promise<void> {
    return this.driver.close();
  }

  [Symbol.asyncDispose](): Promise<void> {
    return this.terminate();
  }
}
